/**
 * machineStore — 机器状态管理（Setup Store）
 *
 * 持久化字段：code / storeName / storeId / maintenanceMode
 *
 * F.2 变更：
 *   - 新增 refreshProfile() → GET /api/kiosk/profile（替换 mock）
 *   - 新增 loadingProfile state
 *   - 错误处理：5xx/timeout → 保留旧数据；4xx → 维护态；401 → 重试一次
 *
 * R1 自检：不含 DTU 字段，refreshProfile 响应严格 whitelist
 * R3 自检：不 log 敏感数据（ws host 不完整 log）
 * R5: 所有 HTTP 调用必须走 useApi()
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { logger } from '@/utils/logger'
import { useApi, ApiError } from '@/composables/useApi'
import type { MachineCode } from '@/types/kiosk'
import { toMachineCode } from '@/types/kiosk'

export interface MachineCommandPayload {
  action: 'lock' | 'unlock' | 'reboot' | 'refresh' | 'shell_quit' | 'maintenance'
  reason?: string
}

/** F.2: refreshProfile 响应类型（对应后端 KioskProfileService::profileOf 返回格式） */
interface ProfileApiResponse {
  machine: {
    machine_code: string
    name: string
    kiosk_mode: string
    is_enabled: boolean
    store_id: number
    ip_address?: string
    app_version?: string
    last_heartbeat?: string | null
  }
  store: {
    id: number
    name: string
    contact_phone?: string
    address?: string
  }
  ws: {
    host: string
    port: number
  }
}

export const useMachineStore = defineStore(
  'machine',
  () => {
    // ── state ─────────────────────────────────────────────────────────────────
    const code = ref<MachineCode | null>(null)
    const online = ref(false)
    const storeName = ref('')
    const storePhone = ref('')
    const storeId = ref<number | null>(null)
    const lastHeartbeatAt = ref(0)
    const maintenanceMode = ref(false)
    const lockReason = ref<string | null>(null)
    // F.2 新增：profile 扩展字段 + 加载状态
    const wsHost = ref('')
    const wsPort = ref(8282)
    const kioskMode = ref<string>('CARDSHOP')
    const loadingProfile = ref(false)

    // ── getters ───────────────────────────────────────────────────────────────
    const isReady = computed(
      () => code.value !== null && online.value && !maintenanceMode.value,
    )
    const isLocked = computed(() => lockReason.value !== null)

    // ── F.2: refreshProfile — GET /api/kiosk/profile ─────────────────────────
    /**
     * 拉取机台 Profile（F.2 联调，替换 mock）
     *
     * 错误处理策略（B.2）：
     *   - 401 → 重试一次（最多）
     *   - 5xx / timeout → 保留旧数据
     *   - 4xx (404) → 进入维护模式（机台未注册）
     *
     * R1: 响应不含 dtu_* / player_id / agent_id（后端 whitelist 保证）
     * R5: 必须走 useApi()
     */
    async function refreshProfile(machineCode?: string): Promise<void> {
      if (loadingProfile.value) return // 防重入
      loadingProfile.value = true

      const mc = machineCode ?? code.value ?? (localStorage.getItem('kiosk_machine_code') ?? '')
      if (!mc) {
        loadingProfile.value = false
        logger.warn('[machineStore] refreshProfile: machine_code 未设置，跳过')
        return
      }

      let retried = false

      const doFetch = async (): Promise<void> => {
        const api = useApi()
        const resp = await api.get<ProfileApiResponse>('/api/kiosk/profile', {
          params: { machine_code: mc },
          timeout: 8000,
        })
        const data = resp.data

        // R1 防御：验证响应不含 dtu_* 字段
        const machineData = data?.machine ?? {}
        const dtuKeys = Object.keys(machineData).filter((k) => k.startsWith('dtu_'))
        if (dtuKeys.length > 0) {
          logger.warn('[machineStore] R1 警告：profile 响应含 dtu_* 字段，已忽略', {
            dtu_keys: dtuKeys,
          })
        }

        // 更新机台状态（仅 whitelist 字段）
        if (data?.machine?.machine_code) {
          try {
            code.value = toMachineCode(data.machine.machine_code)
          } catch {
            // 格式异常，保留旧值
          }
        }
        kioskMode.value = data?.machine?.kiosk_mode ?? 'CARDSHOP'

        // 更新门店信息
        storeName.value = data?.store?.name ?? ''
        storePhone.value = data?.store?.contact_phone ?? ''
        storeId.value = data?.store?.id ?? null

        // 更新 ws 配置（R3: 不 log ws host 完整信息）
        wsHost.value = data?.ws?.host ?? ''
        wsPort.value = data?.ws?.port ?? 8282

        // R3: 只 log 机台码 mask + store_id（不 log ws host / port 细节）
        const mcStr = String(mc)
        const mask = mcStr.length > 8
          ? mcStr.slice(0, 4) + '****' + mcStr.slice(-4)
          : mcStr
        logger.info('[machineStore] refreshProfile 成功', {
          machine_mask: mask,
          store_id: storeId.value,
          kiosk_mode: kioskMode.value,
        })
      }

      try {
        await doFetch()
      } catch (err) {
        if (err instanceof ApiError && err.statusCode === 401 && !retried) {
          retried = true
          logger.warn('[machineStore] refreshProfile 401，清 token 后重试一次')
          localStorage.removeItem('kiosk_token')
          try {
            await doFetch()
          } catch (retryErr) {
            logger.error('[machineStore] refreshProfile 重试失败，保留旧数据', {
              status: retryErr instanceof ApiError ? retryErr.statusCode : 0,
            })
          }
        } else if (err instanceof ApiError && err.statusCode === 404) {
          // 404 机台未注册 → 进入维护模式
          maintenanceMode.value = true
          lockReason.value = '机台未注册'
          logger.warn('[machineStore] refreshProfile 404，机台未注册，进入维护模式')
        } else if (err instanceof ApiError && err.statusCode >= 400 && err.statusCode < 500) {
          // 其他 4xx → 保留旧数据
          logger.warn('[machineStore] refreshProfile 4xx，保留旧数据', { status: err.statusCode })
        } else {
          // 5xx / timeout / 网络错误 → 保留旧数据
          logger.error('[machineStore] refreshProfile 失败，保留旧数据', {
            status: err instanceof ApiError ? err.statusCode : 0,
            msg: err instanceof Error ? err.message : String(err),
          })
        }
      } finally {
        loadingProfile.value = false
      }
    }

    // ── 原有 actions ──────────────────────────────────────────────────────────

    function setCode(raw: string): void {
      try {
        code.value = toMachineCode(raw)
        logger.info('[machineStore] 机器码已设置', { code: raw })
      } catch (err) {
        logger.warn('[machineStore] 机器码格式无效', { error: String(err) })
        throw err
      }
    }

    function setOnline(v: boolean): void {
      online.value = v
      logger.info('[machineStore] WS 在线状态变更', { online: v })
    }

    function setMaintenance(v: boolean, reason?: string): void {
      maintenanceMode.value = v
      if (v && reason) {
        lockReason.value = reason
      } else if (!v) {
        lockReason.value = null
      }
      logger.info('[machineStore] 维护模式变更', { maintenanceMode: v, reason })
    }

    function applyMachineCommand(cmd: MachineCommandPayload): void {
      logger.info('[machineStore] 收到机器指令', { action: cmd.action })
      switch (cmd.action) {
        case 'lock':
          lockReason.value = cmd.reason ?? '远程锁机'
          // 立即跳转到维护页（不依赖路由守卫被动拦截）
          import('@/router').then(({ router }) => {
            if (router.currentRoute.value.name !== 'maintenance') {
              router.replace({ name: 'maintenance' })
            }
          }).catch(() => {})
          break
        case 'unlock':
          lockReason.value = null
          // 解锁后跳回首页
          import('@/router').then(({ router }) => {
            if (router.currentRoute.value.name === 'maintenance') {
              router.replace({ name: 'home' })
            }
          }).catch(() => {})
          break
        case 'refresh':
          logger.info('[machineStore] 执行刷新页面')
          import('@/utils/tauri-bridge').then(({ refreshApp }) => {
            refreshApp()
          }).catch(() => {
            window.location.reload()
          })
          break
        case 'reboot':
          logger.warn('[machineStore] 执行重启应用')
          import('@/utils/tauri-bridge').then(({ restartApp }) => {
            restartApp()
          }).catch(() => {
            window.location.reload()
          })
          break
        case 'shell_quit':
          logger.warn('[machineStore] 执行退出应用')
          import('@/utils/tauri-bridge').then(({ quitApp }) => {
            quitApp()
          }).catch((err: unknown) => {
            logger.warn('[machineStore] quitApp 失败', { error: String(err) })
          })
          break
        case 'maintenance':
          maintenanceMode.value = true
          lockReason.value = cmd.reason ?? '维护中'
          break
      }
    }

    function reset(): void {
      code.value = null
      online.value = false
      storeName.value = ''
      storeId.value = null
      lastHeartbeatAt.value = 0
      maintenanceMode.value = false
      lockReason.value = null
      wsHost.value = ''
      wsPort.value = 8282
      kioskMode.value = 'CARDSHOP'
      loadingProfile.value = false
      logger.info('[machineStore] 状态已重置')
    }

    return {
      code,
      online,
      storeName,
      storePhone,
      storeId,
      lastHeartbeatAt,
      maintenanceMode,
      lockReason,
      wsHost,
      wsPort,
      kioskMode,
      loadingProfile,
      isReady,
      isLocked,
      refreshProfile,
      setCode,
      setOnline,
      setMaintenance,
      applyMachineCommand,
      reset,
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { persist: { paths: ['code', 'storeName', 'storePhone', 'storeId', 'maintenanceMode', 'wsHost', 'wsPort', 'kioskMode'] } } as any,
)
